'use client'

// React
import { useState, useEffect, useRef } from 'react'

// Next.js
import { useRouter } from 'next/navigation'

// Lib
import { DocNavItem, SearchIndexItem } from '../../lib/docs-nav'

interface DocSearchProps {
  navigation: DocNavItem[]
  searchIndex: SearchIndexItem[]
}

interface SearchResult {
  title: string
  path: string
  section: string
}

export function DocSearch({
  navigation: _navigation,
  searchIndex: providedSearchIndex,
}: DocSearchProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  // Search function
  useEffect(() => {
    if (query.trim() === '') {
      setResults([])
      setIsOpen(false)
      return
    }

    const searchQuery = query.toLowerCase()
    const filtered = providedSearchIndex
      .map((item) => {
        // Calculate relevance score
        const titleMatch = item.title.toLowerCase().includes(searchQuery)
        const sectionMatch = item.section.toLowerCase().includes(searchQuery)
        const contentMatch = item.content
          ? item.content.toLowerCase().includes(searchQuery)
          : false

        // Prioritize title matches, then section, then content
        let score = 0
        if (titleMatch) score += 10
        if (sectionMatch) score += 5
        if (contentMatch) score += 1

        return {
          item,
          score,
          hasMatch: titleMatch || sectionMatch || contentMatch,
        }
      })
      .filter((result) => result.hasMatch)
      .sort((a, b) => b.score - a.score) // Sort by relevance
      .map((result) => ({
        title: result.item.title,
        path: result.item.path,
        section: result.item.section,
      }))

    setResults(filtered.slice(0, 8)) // Limit to 8 results
    setIsOpen(filtered.length > 0)
    setSelectedIndex(0)
  }, [query, providedSearchIndex])

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault()
      handleSelect(results[selectedIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const handleSelect = (result: SearchResult) => {
    router.push(result.path)
    setQuery('')
    setIsOpen(false)
    inputRef.current?.blur()
  }

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        !inputRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className="relative mb-6">
      {/* Search Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg
            className="h-4 w-4 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          ref={inputRef}
          id="doc-search"
          name="doc-search"
          type="search"
          placeholder="Search docs..."
          aria-label="Search docs"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(true)}
          className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-10 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:ring-blue-400 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('')
              setIsOpen(false)
            }}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            aria-label="Clear search"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div
          ref={resultsRef}
          className="absolute z-50 mt-2 w-full rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="max-h-96 overflow-y-auto py-2">
            {results.map((result, index) => (
              <button
                key={result.path}
                onClick={() => handleSelect(result)}
                className={`flex w-full flex-col px-4 py-2 text-left transition-colors ${
                  index === selectedIndex
                    ? 'bg-blue-50 dark:bg-slate-700'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {result.title}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-300">
                  {result.section}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
